import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, Printer, RotateCcw, Search } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { Button } from "@/components/ui/button";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { formatBDT } from "@/lib/bdCurrency";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  BD_HS_PRESETS,
  BD_FTA_COUNTRIES,
  DUTY_LAST_UPDATED,
  HS_CODE_FINDER_URL,
  NBR_CUSTOMS_URL,
  type HsCodePreset,
} from "@/data/bdHsCodes";
import { computeCif, computeDuty, type DutyRates } from "@/lib/calculators/duty";

type B = { en: string; bn: string };

const T = {
  h1: { en: "Bangladesh Customs Duty Calculator", bn: "বাংলাদেশ কাস্টমস ডিউটি ক্যালকুলেটর" },
  desc: {
    en: "Estimate Bangladesh import duty stack — Customs Duty, Regulatory Duty, Supplementary Duty, VAT, AIT and AT — with HS code presets and country-of-origin FTA preferences.",
    bn: "বাংলাদেশের আমদানি শুল্ক — CD, RD, SD, VAT, AIT, AT — HS কোড প্রিসেট ও উৎসদেশ FTA সুবিধাসহ হিসাব করুন।",
  },

  preset: { en: "Quick presets", bn: "দ্রুত প্রিসেট" },
  search: { en: "Search HS code or item name", bn: "HS কোড বা পণ্য খুঁজুন" },
  origin: { en: "Country of origin", bn: "উৎসদেশ" },
  fta: { en: "FTA", bn: "FTA" },
  cif: { en: "Assessable value (CIF in BDT)", bn: "মূল্যায়ন মূল্য (CIF, ৳)" },

  cifBuilder: { en: "Build CIF from invoice", bn: "ইনভয়েস থেকে CIF বের করুন" },
  invoice: { en: "Invoice value (foreign)", bn: "ইনভয়েস মূল্য (বিদেশী)" },
  fx: { en: "Exchange rate (BDT per unit)", bn: "এক্সচেঞ্জ রেট (প্রতি ইউনিটে ৳)" },
  freight: { en: "Freight (BDT)", bn: "মালবাহন (৳)" },
  insurance: { en: "Insurance (BDT)", bn: "বীমা (৳)" },
  applyCif: { en: "Use as CIF", bn: "CIF হিসাবে নিন" },
  computedCif: { en: "Computed CIF", bn: "গণিত CIF" },

  rates: { en: "Duty rates (editable)", bn: "শুল্ক হার (সম্পাদনযোগ্য)" },
  cd: { en: "Customs Duty (CD)", bn: "কাস্টমস ডিউটি (CD)" },
  rd: { en: "Regulatory Duty (RD)", bn: "রেগুলেটরি ডিউটি (RD)" },
  sd: { en: "Supplementary Duty (SD)", bn: "সম্পূরক শুল্ক (SD)" },
  vat: { en: "VAT", bn: "ভ্যাট" },
  ait: { en: "Advance Income Tax (AIT)", bn: "অগ্রিম আয়কর (AIT)" },
  at: { en: "Advance Tax (AT)", bn: "অগ্রিম কর (AT)" },
  aitOnAv: { en: "AIT on Assessable Value only", bn: "AIT শুধু মূল্যায়ন মূল্যে" },
  atOnAv: { en: "AT on Assessable Value only", bn: "AT শুধু মূল্যায়ন মূল্যে" },

  totalDuty: { en: "Total duty payable", bn: "মোট শুল্ক" },
  landed: { en: "Landed cost", bn: "ল্যান্ডেড খরচ" },
  effective: { en: "Effective duty rate", bn: "কার্যকর শুল্ক হার" },
  breakdown: { en: "Detailed breakdown", bn: "বিস্তারিত বিভাজন" },
  duty: { en: "Duty type", bn: "শুল্কের ধরন" },
  rate: { en: "Rate", bn: "হার" },
  base: { en: "Base amount", bn: "ভিত্তি" },
  amount: { en: "Duty amount", bn: "শুল্ক" },
  total: { en: "Total", bn: "মোট" },

  reset: { en: "Reset", bn: "রিসেট" },
  print: { en: "Print", bn: "প্রিন্ট" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
} as const;

const DUTY_LABEL: Record<"cd" | "rd" | "sd" | "vat" | "ait" | "at", B> = {
  cd: T.cd,
  rd: T.rd,
  sd: T.sd,
  vat: T.vat,
  ait: T.ait,
  at: T.at,
};

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How is import duty calculated in Bangladesh?", bn: "বাংলাদেশে আমদানি শুল্ক কীভাবে গণনা করা হয়?" },
    a: {
      en: "Bangladesh uses a cascading duty stack on the assessable value (CIF). Customs Duty (CD) and Regulatory Duty (RD) apply on CIF. Supplementary Duty (SD) applies on (CIF + CD + RD). VAT applies on (CIF + CD + RD + SD). AIT and AT typically apply on the cumulative base. Total duty = CD + RD + SD + VAT + AIT + AT.",
      bn: "বাংলাদেশের শুল্ক ক্যাসকেডিং পদ্ধতিতে গণনা হয়। CD ও RD প্রযোজ্য CIF এর উপর। SD প্রযোজ্য (CIF + CD + RD) এর উপর। VAT (CIF + CD + RD + SD) এর উপর। AIT ও AT সাধারণত ক্রমবর্ধমান ভিত্তির উপর। মোট = CD + RD + SD + VAT + AIT + AT।",
    },
  },
  {
    q: { en: "What is HS Code?", bn: "HS কোড কী?" },
    a: {
      en: "Harmonized System (HS) Code is an internationally standardized 6-digit (extended to 8 digits in Bangladesh) classification for traded goods. NBR uses HS codes to determine duty rates. You can look up your HS code on Bangladesh Customs' Tariff Schedule.",
      bn: "Harmonized System কোড হলো আন্তর্জাতিক ৬-অঙ্কের (বাংলাদেশে ৮-অঙ্ক) পণ্য শ্রেণিবিন্যাস। NBR HS কোড অনুযায়ী শুল্ক হার নির্ধারণ করে। বাংলাদেশ কাস্টমসের ট্যারিফ শিডিউলে কোড খুঁজে পাবেন।",
    },
  },
  {
    q: { en: "What is CIF and how is it different from FOB?", bn: "CIF কী এবং FOB থেকে আলাদা কীভাবে?" },
    a: {
      en: "CIF = Cost + Insurance + Freight, the assessable value used by Bangladesh Customs for duty calculation. FOB = Free On Board, only the cost of goods loaded at the port of origin. Duty is always on CIF — so you must add freight and insurance even if your invoice is FOB.",
      bn: "CIF = পণ্যমূল্য + বীমা + মালবাহন, বাংলাদেশ কাস্টমসের মূল্যায়ন ভিত্তি। FOB = বন্দরে লোড করার পর্যন্ত শুধু পণ্যমূল্য। শুল্ক সর্বদা CIF এর উপর — FOB ইনভয়েসেও মালবাহন ও বীমা যোগ করতে হবে।",
    },
  },
  {
    q: { en: "Mobile phone import duty Bangladesh?", bn: "মোবাইল ফোনের আমদানি শুল্ক?" },
    a: {
      en: "Smartphones (HS 8517.13.00): typically CD 25%, RD 3%, VAT 15%, AIT 5%. On a ৳50,000 phone the effective duty is roughly 54%. Local assembly (CKD/SKD) attracts much lower duty to encourage local industry.",
      bn: "স্মার্টফোন (HS 8517.13.00): সাধারণত CD ২৫%, RD ৩%, VAT ১৫%, AIT ৫%। ৳৫০,০০০ ফোনে কার্যকর শুল্ক প্রায় ৫৪%। স্থানীয় সংযোজন (CKD/SKD) এ অনেক কম।",
    },
  },
  {
    q: { en: "মোবাইল ইম্পোর্ট করতে কত ডিউটি লাগে?", bn: "মোবাইল ইম্পোর্ট করতে কত ডিউটি লাগে?" },
    a: {
      en: "সাধারণত ফোনের CIF মূল্যের প্রায় ৫০-৫৭% মোট শুল্ক — CD ২৫%, RD ৩%, VAT ১৫%, AIT ৫%। উপরের ক্যালকুলেটরে আপনার মূল্য দিয়ে সঠিক হিসাব দেখুন।",
      bn: "সাধারণত ফোনের CIF মূল্যের প্রায় ৫০-৫৭% মোট শুল্ক — CD ২৫%, RD ৩%, VAT ১৫%, AIT ৫%। উপরের ক্যালকুলেটরে আপনার মূল্য দিয়ে সঠিক হিসাব দেখুন।",
    },
  },
  {
    q: { en: "কাস্টমস ডিউটি কীভাবে গণনা করা হয়?", bn: "কাস্টমস ডিউটি কীভাবে গণনা করা হয়?" },
    a: {
      en: "প্রথমে CIF নির্ণয় করুন, তারপর HS কোড অনুযায়ী CD, RD, SD, VAT, AIT, AT হার ক্যাসকেডিং পদ্ধতিতে প্রয়োগ করুন।",
      bn: "প্রথমে CIF নির্ণয় করুন, তারপর HS কোড অনুযায়ী CD, RD, SD, VAT, AIT, AT হার ক্যাসকেডিং পদ্ধতিতে প্রয়োগ করুন।",
    },
  },
  {
    q: { en: "Car import duty in Bangladesh?", bn: "গাড়ির ইম্পোর্ট ডিউটি কত?" },
    a: {
      en: "Cars attract very high duty driven by Supplementary Duty by engine CC: 1000-1500cc ~45% SD, 1500-2000cc ~100% SD, above 3000cc up to 500% SD. Combined with CD 25%, VAT 15%, AIT 5%, AT 5%, effective duty often exceeds 200% on luxury cars.",
      bn: "গাড়িতে সম্পূরক শুল্ক ইঞ্জিন সিসি অনুযায়ী খুব বেশি: ১০০০-১৫০০ সিসি ~৪৫%, ১৫০০-২০০০ ~১০০%, ৩০০০+ ~৫০০%। CD ২৫%, VAT ১৫%, AIT ৫%, AT ৫% মিলে বিলাসী গাড়িতে কার্যকর শুল্ক ২০০% এর বেশি হতে পারে।",
    },
  },
  {
    q: { en: "গাড়ির ইম্পোর্ট ডিউটি কত?", bn: "গাড়ির ইম্পোর্ট ডিউটি কত?" },
    a: {
      en: "ইঞ্জিন সিসি অনুযায়ী সম্পূরক শুল্কে অনেক পার্থক্য — উপরের প্রিসেট থেকে আপনার গাড়ির সিসি অনুযায়ী হিসাব দেখুন।",
      bn: "ইঞ্জিন সিসি অনুযায়ী সম্পূরক শুল্কে অনেক পার্থক্য — উপরের প্রিসেট থেকে আপনার গাড়ির সিসি অনুযায়ী হিসাব দেখুন।",
    },
  },
  {
    q: { en: "Does country of origin matter?", bn: "উৎসদেশ কি গুরুত্বপূর্ণ?" },
    a: {
      en: "Yes. Bangladesh has FTA / preferential trade agreements: SAFTA (India, Pakistan, Sri Lanka, Bhutan, Nepal, Maldives, Afghanistan) often gives 100% CD reduction; APTA (China, Korea) gives partial preference. A valid Certificate of Origin is required to claim the preference.",
      bn: "হ্যাঁ। বাংলাদেশের FTA: SAFTA (ভারত, পাকিস্তান, শ্রীলঙ্কা, ভুটান, নেপাল, মালদ্বীপ, আফগানিস্তান) সাধারণত ১০০% CD ছাড়, APTA (চীন, কোরিয়া) আংশিক ছাড়। বৈধ Certificate of Origin আবশ্যক।",
    },
  },
  {
    q: { en: "Are there baggage allowances for travelers?", bn: "যাত্রীদের জন্য ব্যাগেজ সুবিধা আছে?" },
    a: {
      en: "Yes. Returning Bangladeshis enjoy a baggage rules allowance — duty-free for personal items up to a limit, and one mobile phone is duty-free per passenger. Larger items like TVs, refrigerators, motorcycles attract reduced 'Baggage Rules' duty rather than full commercial duty.",
      bn: "হ্যাঁ। ফেরত আসা বাংলাদেশিদের জন্য ব্যাগেজ রুলস সুবিধা — সীমার মধ্যে ব্যক্তিগত পণ্য শুল্কমুক্ত, প্রতি যাত্রী ১টি মোবাইল শুল্কমুক্ত। টিভি, ফ্রিজ, মোটরসাইকেলে ব্যাগেজ রুলসে কম শুল্ক।",
    },
  },
  {
    q: { en: "Where can I find official HS codes and rates?", bn: "অফিসিয়াল HS কোড ও হার কোথায় পাব?" },
    a: {
      en: "Use the Bangladesh Customs Tariff Schedule on bangladeshcustoms.gov.bd or NBR (nbr.gov.bd). Rates change with the annual budget (SROs) — always verify before clearing a shipment.",
      bn: "bangladeshcustoms.gov.bd এর Tariff Schedule অথবা nbr.gov.bd ব্যবহার করুন। হার বার্ষিক বাজেটে (SRO) পরিবর্তন হয় — চালান ছাড়ানোর আগে যাচাই করুন।",
    },
  },
  {
    q: { en: "Can I clear customs without a C&F agent?", bn: "C&F এজেন্ট ছাড়া কাস্টমস ক্লিয়ার করা যায়?" },
    a: {
      en: "Personal/baggage clearances and very small parcels may be self-cleared. Commercial shipments through Chattogram, Mongla or ICDs almost always require a licensed Clearing & Forwarding (C&F) agent. This calculator helps you estimate duty independently of any agent.",
      bn: "ব্যক্তিগত/ব্যাগেজ ও খুব ছোট পার্সেল নিজে ক্লিয়ার করা যেতে পারে। চট্টগ্রাম, মোংলা বা ICD-তে বাণিজ্যিক চালানে লাইসেন্সধারী C&F এজেন্ট প্রায় বাধ্যতামূলক। এই ক্যালকুলেটর এজেন্ট ছাড়াই অনুমান করতে সাহায্য করে।",
    },
  },
];

export const Route = createFileRoute("/duty-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Customs Duty Calculator BD — CD, SD, VAT, AIT",
      description:
        "Calculate Bangladesh import customs duty: CD, SD, VAT, AIT, RD. Mobile phone, laptop, car import duty calculator. ইম্পোর্ট ডিউটি ক্যালকুলেটর বাংলাদেশ।",
      path: "/duty-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Customs Duty Calculator", path: "/duty-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: DutyCalculatorPage,
});

function DutyCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);

  const [presetCode, setPresetCode] = useState<string>(BD_HS_PRESETS[0].hsCode);
  const preset = BD_HS_PRESETS.find((p) => p.hsCode === presetCode) ?? BD_HS_PRESETS[0];

  const [searchStr, setSearchStr] = useState("");
  const filteredPresets = useMemo(() => {
    const q = searchStr.trim().toLowerCase();
    if (!q) return BD_HS_PRESETS;
    return BD_HS_PRESETS.filter(
      (p) =>
        p.hsCode.toLowerCase().includes(q) ||
        p.description.en.toLowerCase().includes(q) ||
        p.description.bn.includes(searchStr.trim()),
    );
  }, [searchStr]);

  const [origin, setOrigin] = useState<string>("OTHER");
  const fta = BD_FTA_COUNTRIES.find((c) => c.code === origin) ?? BD_FTA_COUNTRIES[BD_FTA_COUNTRIES.length - 1];

  // Rates seeded from preset, editable
  const [rates, setRates] = useState<DutyRates>(toRates(preset));

  // Whenever preset changes, reseed rates
  const seedFromPreset = (p: HsCodePreset) => {
    setPresetCode(p.hsCode);
    setRates(toRates(p));
  };

  // Apply FTA reduction on CD
  const cdAfterFta = rates.cd * (1 - fta.cdReductionPct / 100);
  const effectiveRates: DutyRates = { ...rates, cd: cdAfterFta };

  // Assessable value
  const [avStr, setAvStr] = useState("50000");
  const av = Math.max(0, parseFloat(avStr) || 0);

  // CIF builder
  const [invoice, setInvoice] = useState("500");
  const [fx, setFx] = useState("110");
  const [freight, setFreight] = useState("2000");
  const [insurance, setInsurance] = useState("500");
  const computedCif = computeCif({
    invoiceForeign: parseFloat(invoice) || 0,
    exchangeRate: parseFloat(fx) || 0,
    freight: parseFloat(freight) || 0,
    insurance: parseFloat(insurance) || 0,
  });

  const result = useMemo(() => computeDuty(av, effectiveRates), [av, effectiveRates]);

  const useBn = lang === "bn" || bnDigits;
  const money = (v: number, frac = 0) => formatBDT(Math.round(v * 100) / 100, useBn ? "bn" : "en", frac);
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));

  const reset = () => {
    seedFromPreset(BD_HS_PRESETS[0]);
    setOrigin("OTHER");
    setAvStr("50000");
    setSearchStr("");
  };

  return (
    <CalculatorLayout
      slug="duty-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Package"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Disclaimer banner */}
      <div className="flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 text-xs text-yellow-700 dark:text-yellow-400">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <p className={cn(lang === "bn" && "font-bn")}>
          {lang === "en"
            ? `For estimation only. Actual duty depends on HS classification, country of origin, FTA preferences and Customs assessment. Verify with `
            : `শুধুমাত্র অনুমানের জন্য। প্রকৃত শুল্ক HS শ্রেণিবিন্যাস, উৎসদেশ, FTA সুবিধা ও কাস্টমসের নির্ধারণের উপর নির্ভর করে। যাচাই করুন `}
          <a href={NBR_CUSTOMS_URL} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
            NBR Customs
          </a>
          {lang === "en" ? ` or your C&F agent. Last updated ${DUTY_LAST_UPDATED}.` : ` বা আপনার C&F এজেন্টের সাথে। সর্বশেষ আপডেট ${DUTY_LAST_UPDATED}।`}
        </p>
      </div>

      {/* HS code search */}
      <div className="mt-6 space-y-2">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("preset")}</p>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchStr}
            onChange={(e) => setSearchStr(e.target.value)}
            placeholder={tr("search")}
            className={cn(
              "w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm",
              lang === "bn" && "font-bn",
            )}
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filteredPresets.slice(0, 20).map((p) => {
            const active = presetCode === p.hsCode;
            return (
              <button
                key={p.hsCode}
                type="button"
                onClick={() => seedFromPreset(p)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                  active
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-foreground/80 hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {p.description[lang]} <span className="opacity-60">· {p.hsCode}</span>
              </button>
            );
          })}
          {filteredPresets.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {lang === "en" ? "No matches — enter rates manually below." : "কোনো ম্যাচ নেই — নিচে নিজে হার দিন।"}
            </p>
          )}
        </div>
        {preset.notes && (
          <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>ℹ {preset.notes[lang]}</p>
        )}
      </div>

      {/* Country / FTA */}
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <div>
          <label className={cn("mb-1 block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("origin")}</label>
          <select
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            className={cn(
              "w-full rounded-md border border-border bg-background px-3 py-2 text-sm",
              lang === "bn" && "font-bn",
            )}
          >
            {BD_FTA_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name[lang]} — {c.pact}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
            {tr("fta")}: <strong>{fta.pact}</strong>
            {fta.cdReductionPct > 0 && (
              <>
                {" — "}
                {lang === "en"
                  ? `CD reduced by ${fta.cdReductionPct}% (Cert. of Origin required)`
                  : `CD ${num(fta.cdReductionPct)}% হ্রাস (Certificate of Origin আবশ্যক)`}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Assessable value */}
      <div className="mt-6">
        <NumberInput label={tr("cif")} value={avStr} onChange={setAvStr} unit="৳" />
      </div>

      {/* CIF builder */}
      <details className="mt-4 rounded-xl border border-border bg-card">
        <summary className={cn("cursor-pointer select-none px-4 py-3 text-sm font-semibold", lang === "bn" && "font-bn")}>
          {tr("cifBuilder")}
        </summary>
        <div className="space-y-3 border-t border-border p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <NumberInput label={tr("invoice")} value={invoice} onChange={setInvoice} unit="USD" />
            <NumberInput label={tr("fx")} value={fx} onChange={setFx} unit="৳" />
            <NumberInput label={tr("freight")} value={freight} onChange={setFreight} unit="৳" />
            <NumberInput label={tr("insurance")} value={insurance} onChange={setInsurance} unit="৳" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className={cn("text-sm", lang === "bn" && "font-bn")}>
              {tr("computedCif")}: <strong className="tabular-nums">{money(computedCif)}</strong>
            </p>
            <Button size="sm" variant="outline" onClick={() => setAvStr(String(Math.round(computedCif)))}>
              {tr("applyCif")}
            </Button>
          </div>
        </div>
      </details>

      {/* Rates editor */}
      <div className="mt-6 space-y-3">
        <p className={cn("text-sm font-medium", lang === "bn" && "font-bn")}>{tr("rates")}</p>
        <div className="grid gap-3 md:grid-cols-3">
          <RateField label={tr("cd")} value={rates.cd} onChange={(v) => setRates({ ...rates, cd: v })} />
          <RateField label={tr("rd")} value={rates.rd} onChange={(v) => setRates({ ...rates, rd: v })} />
          <RateField label={tr("sd")} value={rates.sd} onChange={(v) => setRates({ ...rates, sd: v })} max={1000} />
          <RateField label={tr("vat")} value={rates.vat} onChange={(v) => setRates({ ...rates, vat: v })} />
          <RateField label={tr("ait")} value={rates.ait} onChange={(v) => setRates({ ...rates, ait: v })} />
          <RateField label={tr("at")} value={rates.at} onChange={(v) => setRates({ ...rates, at: v })} />
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <label className={cn("inline-flex items-center gap-1.5", lang === "bn" && "font-bn")}>
            <input
              type="checkbox"
              checked={!!rates.aitOnAv}
              onChange={(e) => setRates({ ...rates, aitOnAv: e.target.checked })}
              className="h-3.5 w-3.5 accent-accent"
            />
            {tr("aitOnAv")}
          </label>
          <label className={cn("inline-flex items-center gap-1.5", lang === "bn" && "font-bn")}>
            <input
              type="checkbox"
              checked={!!rates.atOnAv}
              onChange={(e) => setRates({ ...rates, atOnAv: e.target.checked })}
              className="h-3.5 w-3.5 accent-accent"
            />
            {tr("atOnAv")}
          </label>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input
            type="checkbox"
            checked={bnDigits}
            onChange={(e) => setBnDigits(e.target.checked)}
            className="h-3.5 w-3.5 accent-accent"
          />
          {tr("bnDigits")}
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="gap-2">
            <Printer size={14} /> {tr("print")}
          </Button>
          <Button variant="outline" onClick={reset} className="gap-2">
            <RotateCcw size={14} /> {tr("reset")}
          </Button>
        </div>
      </div>

      {/* Results */}
      <motion.div
        id="duty-results"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 space-y-6"
      >
        <div className="grid gap-3 md:grid-cols-4">
          <ResultCard label={tr("cif")} value={money(result.assessableValue)} />
          <ResultCard label={tr("totalDuty")} value={money(result.totalDuty)} accent />
          <ResultCard label={tr("landed")} value={money(result.landedCost)} />
          <ResultCard label={tr("effective")} value={`${num(result.effectiveRatePct.toFixed(1))}%`} />
        </div>

        {/* Stacked bar */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-muted-foreground/60" />{tr("cif")}</span>
            {result.lines.map((l) =>
              l.amount > 0 ? (
                <span key={l.key}>
                  <span className={cn("mr-1 inline-block h-2 w-2 rounded-sm", colorFor(l.key))} />
                  {DUTY_LABEL[l.key][lang]}
                </span>
              ) : null,
            )}
          </div>
          <div className="flex h-4 overflow-hidden rounded-full bg-muted">
            <div className="bg-muted-foreground/60" style={{ width: `${pct(result.assessableValue, result.landedCost)}%` }} />
            {result.lines.map((l) =>
              l.amount > 0 ? (
                <div key={l.key} className={colorFor(l.key)} style={{ width: `${pct(l.amount, result.landedCost)}%` }} />
              ) : null,
            )}
          </div>
        </div>

        {/* Breakdown */}
        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("breakdown")}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("duty")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("rate")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("base")}</th>
                  <th className={cn("px-3 py-2 text-right", lang === "bn" && "font-bn")}>{tr("amount")}</th>
                </tr>
              </thead>
              <tbody>
                {result.lines.map((l) => (
                  <tr key={l.key} className="border-t border-border">
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{DUTY_LABEL[l.key][lang]}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(l.ratePct.toFixed(2))}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(l.base)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(l.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                  <td className={cn("px-3 py-2", lang === "bn" && "font-bn")} colSpan={3}>{tr("total")}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(result.totalDuty)}</td>
                </tr>
                <tr className="border-t border-border bg-accent/5 font-semibold text-accent">
                  <td className={cn("px-3 py-2", lang === "bn" && "font-bn")} colSpan={3}>{tr("landed")}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(result.landedCost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "How import duty works in Bangladesh" : "বাংলাদেশে আমদানি শুল্ক কীভাবে কাজ করে"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangladesh Customs (under NBR) levies a layered set of duties on imported goods under the Customs Act and annual SROs. The duty base is the assessable value (CIF). On top of that, several taxes cascade — each one calculated on a base that includes the previous duty."
              : "বাংলাদেশ কাস্টমস (NBR এর অধীনে) আমদানি পণ্যে কাস্টমস আইন ও বার্ষিক SRO অনুযায়ী স্তরভিত্তিক শুল্ক আরোপ করে। শুল্কের ভিত্তি হলো মূল্যায়ন মূল্য (CIF)। তার উপর কয়েকটি শুল্ক ক্যাসকেডিং পদ্ধতিতে যুক্ত হয়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Components of Bangladesh customs duty" : "বাংলাদেশের কাস্টমস শুল্কের উপাদান"} lang={lang}>
          <ul className={cn("list-disc space-y-1.5 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li><strong>CD (Customs Duty):</strong> {lang === "en" ? "primary import duty, 0–25% by HS code." : "প্রধান আমদানি শুল্ক, HS কোড অনুযায়ী ০-২৫%।"}</li>
            <li><strong>RD (Regulatory Duty):</strong> {lang === "en" ? "0% / 3% / 4% on selected items." : "নির্দিষ্ট পণ্যে ০%/৩%/৪%।"}</li>
            <li><strong>SD (Supplementary Duty):</strong> {lang === "en" ? "0–500% on luxury / discouraged goods." : "বিলাসী/নিরুৎসাহিত পণ্যে ০-৫০০%।"}</li>
            <li><strong>VAT:</strong> {lang === "en" ? "15% standard on (CIF + CD + RD + SD)." : "১৫% সাধারণ, (CIF + CD + RD + SD) এর উপর।"}</li>
            <li><strong>AIT:</strong> {lang === "en" ? "Advance Income Tax, typically 5%." : "অগ্রিম আয়কর, সাধারণত ৫%।"}</li>
            <li><strong>AT:</strong> {lang === "en" ? "Advance Tax, 5% on certain items." : "অগ্রিম কর, কিছু পণ্যে ৫%।"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Country of origin & FTA preferences" : "উৎসদেশ ও FTA সুবিধা"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "SAFTA partners (India, Pakistan, Sri Lanka, Bhutan, Nepal, Maldives, Afghanistan) generally enjoy 100% Customs Duty waiver. APTA (China, Korea) gives partial preference. A valid Certificate of Origin from the exporting country's chamber of commerce is required at clearance."
              : "SAFTA অংশীদার (ভারত, পাকিস্তান, শ্রীলঙ্কা, ভুটান, নেপাল, মালদ্বীপ, আফগানিস্তান) সাধারণত ১০০% CD ছাড় পায়। APTA (চীন, কোরিয়া) আংশিক ছাড়। ক্লিয়ারেন্সের সময় বৈধ Certificate of Origin আবশ্যক।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "CIF vs FOB" : "CIF বনাম FOB"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Bangladesh Customs values goods on a CIF basis. If your invoice is FOB, add freight to destination port and insurance before computing duty. Use the CIF builder above for a quick conversion from foreign currency invoices."
              : "বাংলাদেশ কাস্টমস CIF ভিত্তিতে মূল্য নির্ধারণ করে। FOB ইনভয়েস হলে গন্তব্য বন্দর পর্যন্ত মালবাহন ও বীমা যোগ করুন। উপরের CIF বিল্ডার ব্যবহার করে বিদেশি মুদ্রার ইনভয়েস থেকে দ্রুত হিসাব করুন।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Common HS codes & duty rates" : "জনপ্রিয় HS কোড ও হার"} lang={lang}>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[600px] text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "en" ? "Item" : "পণ্য"}</th>
                  <th className="px-3 py-2 text-left">HS</th>
                  <th className="px-3 py-2 text-right">CD</th>
                  <th className="px-3 py-2 text-right">RD</th>
                  <th className="px-3 py-2 text-right">SD</th>
                  <th className="px-3 py-2 text-right">VAT</th>
                  <th className="px-3 py-2 text-right">AIT</th>
                </tr>
              </thead>
              <tbody>
                {BD_HS_PRESETS.map((p) => (
                  <tr key={p.hsCode} className="border-t border-border">
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{p.description[lang]}</td>
                    <td className="px-3 py-2 tabular-nums">{p.hsCode}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(p.cd)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(p.rd)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(p.sd)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(p.vat)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(p.ait)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {lang === "en" ? "Look up your exact code on the " : "আপনার সঠিক কোড দেখুন "}
            <a href={HS_CODE_FINDER_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
              {lang === "en" ? "Bangladesh Customs Tariff Schedule" : "বাংলাদেশ কাস্টমস ট্যারিফ শিডিউল"}
            </a>.
          </p>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/vat-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">VAT</Link>
            <Link to="/tax-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Income Tax" : "আয়কর"}</Link>
            <Link to="/discount-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Discount" : "ডিসকাউন্ট"}</Link>
            <Link to="/percentage-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Percentage" : "শতকরা"}</Link>
          </div>
        </Section>
      </article>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #duty-results, #duty-results * { visibility: visible !important; }
          #duty-results { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </CalculatorLayout>
  );
}

function toRates(p: HsCodePreset): DutyRates {
  return {
    cd: p.cd,
    rd: p.rd,
    sd: p.sd,
    vat: p.vat,
    ait: p.ait,
    at: p.at,
    aitOnAv: !!p.aitOnAv,
    atOnAv: !!p.atOnAv,
  };
}

function pct(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.max(0, Math.min(100, (part / whole) * 100));
}

function colorFor(k: "cd" | "rd" | "sd" | "vat" | "ait" | "at"): string {
  switch (k) {
    case "cd": return "bg-accent";
    case "rd": return "bg-accent/70";
    case "sd": return "bg-amber-500";
    case "vat": return "bg-emerald-500";
    case "ait": return "bg-sky-500";
    case "at": return "bg-violet-500";
  }
}

function RateField({ label, value, onChange, max = 100 }: { label: string; value: number; onChange: (v: number) => void; max?: number }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={max}
          step={0.1}
          value={value}
          onChange={(e) => onChange(Math.max(0, Math.min(max, parseFloat(e.target.value) || 0)))}
          className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-right text-sm tabular-nums"
        />
        <span className="text-xs text-muted-foreground">%</span>
      </div>
    </div>
  );
}

function ResultCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "rounded-xl border p-4",
        accent ? "border-accent/40 bg-accent/5" : "border-border bg-card",
      )}
    >
      <p className={cn("text-xs font-semibold uppercase tracking-wide", accent ? "text-accent" : "text-muted-foreground")}>{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums md:text-3xl">{value}</p>
    </motion.div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
